import { COUPON_STATUS } from "app/constants";
import AmountContainer from "app/pages/.shared/AmountContainer";
import AmountSign from "app/pages/.shared/AmountSign/AmountSign";
import FloatingButton from "app/pages/.shared/Popover/FloatingButton";
import RelativeLink from "app/pages/.shared/RelativeLink";
import IconInfo from "app/pages/.shared/static/icons/IconInfo";
import {
	sendTagOnHideCreditHistoryClick,
	sendTagOnShowCreditHistoryClick,
} from "app/utils/analytics";
import { scrollToElement } from "app/utils/scroller";
import { isTsCurrentDay } from "app/utils/utils";
import PropTypes from "prop-types";
import { memo, useCallback } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";
import "./MyCouponInfo.scss";

const MyCouponInfo = ({
	uses = [],
	creationDate,
	amount,
	remainingBalance,
	balance,
	type,
	title,
	description,
	label,
	status,
}) => {
	const viewCgu = useCallback(() => {
		scrollToElement({
			element: document.getElementById("credits-cgu"),
			disableSmoothScroll: false,
		});
	}, []);
	const isCouponUsing = status === COUPON_STATUS.USING;
	const shouldDisplayDefaultMessage = !description && uses?.length === 0;
	return (
		<div className="my-coupon-info">
			<FloatingButton
				position="top"
				popoverClassName="my-coupon-info__modal"
				floatingContent={
					<div className="my-coupon-info__content" data-testid="my-coupon-info-content">
						{title && (
							<div
								className="my-coupon-info__title"
								data-testid="my-coupon-info-title"
							>
								{label ? label : title}
							</div>
						)}
						{description && (
							<div
								className="my-coupon-info__description"
								data-testid="my-coupon-info-description"
							>
								{description}
							</div>
						)}
						{shouldDisplayDefaultMessage && (
							<FormattedMessage id="mycoupons.credit.banner.info.description" />
						)}
						{creationDate && uses.length > 0 && (
							<>
								<div
									className="my-coupon-info__history-intro"
									data-testid="my-coupon-info-history-intro"
								>
									<FormattedMessage
										id="mycoupons.credit.history.intro.label"
										values={{
											link: (
												<RelativeLink onClick={viewCgu}>
													<FormattedMessage id="mycoupons.credits.conditions.cta" />
												</RelativeLink>
											),
										}}
									/>
								</div>
								<div className="my-coupon-info__history-list">
									<div className="my-coupon-info__history-item">
										<div className="my-coupon-info__history-label">
											<FormattedMessage
												id="mycoupons.credit.history.creation.label"
												values={{
													date: (
														<FormattedDate
															timeZone="UTC"
															day="2-digit"
															month="2-digit"
															year="numeric"
															value={creationDate}
														/>
													),
												}}
											/>
										</div>
										<div className="my-coupon-info__history-amount">
											<AmountContainer
												amount={amount}
												prefix={<AmountSign sign="+" />}
											/>
										</div>
									</div>

									{uses.map(use => (
										<div
											key={use.date}
											className="my-coupon-info__history-item"
										>
											<div className="my-coupon-info__history-label">
												{isTsCurrentDay(use.date) && isCouponUsing ? (
													<FormattedMessage id="mycoupons.credit.usablecredits.today" />
												) : (
													<FormattedMessage
														id="mycoupons.credit.history.used.label"
														values={{
															date: (
																<FormattedDate
																	timeZone="UTC"
																	day="2-digit"
																	month="2-digit"
																	year="numeric"
																	value={use.date}
																/>
															),
														}}
													/>
												)}
											</div>
											<div className="my-coupon-info__history-amount">
												<AmountContainer
													amount={use.amount}
													prefix={<AmountSign sign="-" />}
												/>
											</div>
										</div>
									))}

									{remainingBalance === 0 && isCouponUsing ? (
										<></>
									) : (
										<div className="my-coupon-info__history-item">
											<div className="my-coupon-info__history-label">
												<FormattedMessage id="mycoupons.credit.history.balance.label" />
											</div>
											<div className="my-coupon-info__history-amount">
												<AmountContainer
													amount={
														isCouponUsing ? remainingBalance : balance
													}
												/>
											</div>
										</div>
									)}
								</div>
							</>
						)}
					</div>
				}
				referenceComponent={
					<IconInfo
						width={15}
						height={15}
						style={{ marginLeft: "5px" }}
						data-testid="my-coupon-info-icon"
						className="my-coupon-info__floating-icon"
					/>
				}
				referenceButtonClassName="my-coupon-info__floating-button"
				floatingOffsetDesk={[150, 10]}
				onClick={() => sendTagOnShowCreditHistoryClick(type)}
				onClickAway={() => sendTagOnHideCreditHistoryClick(type)}
			/>
		</div>
	);
};

MyCouponInfo.propTypes = {
	type: PropTypes.string,
	status: PropTypes.string,
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	creationDate: PropTypes.number,
	amount: PropTypes.number,
	balance: PropTypes.number,
	remainingBalance: PropTypes.number,
	uses: PropTypes.arrayOf(
		PropTypes.shape({
			useDate: PropTypes.number,
			amount: PropTypes.number,
		})
	),
};

export default memo(MyCouponInfo);
